'use client';

import { useState, useEffect } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface NavigationItem {
  name: string;
  href: string;
  icon?: string;
}

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const pathname = usePathname();

  const navigationItems: NavigationItem[] = [
    { name: 'Home', href: '/homepage', icon: 'HomeIcon' },
    { name: 'About Us', href: '/about-us', icon: 'InformationCircleIcon' },
    { name: 'Services', href: '/services-overview', icon: 'CogIcon' },
    { name: 'Contact', href: '/contact-and-support', icon: 'PhoneIcon' },
  ];

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const closeMobileMenu = () => {
    setIsMobileMenuOpen(false);
  };

  const isActivePath = (href: string) => {
    return pathname === href;
  };

  return (
    <>
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled ? 'bg-card shadow-md' : 'bg-card'
        }`}
      >
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between h-16 px-4 sm:px-6 lg:px-8">
            {/* Logo Section */}
            <div className="flex items-center">
              <Link
                href="/homepage"
                className="flex items-center space-x-3 group"
                onClick={closeMobileMenu}
              >
                <div className="relative">
                  <svg
                    width="40"
                    height="40"
                    viewBox="0 0 40 40"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    className="transition-transform duration-300 group-hover:scale-105"
                  >
                    <rect
                      width="40"
                      height="40"
                      rx="8"
                      fill="var(--color-primary)"
                    />
                    <path
                      d="M12 28L20 12L28 28H24L20 20L16 28H12Z"
                      fill="var(--color-primary-foreground)"
                    />
                    <circle
                      cx="20"
                      cy="16"
                      r="2"
                      fill="var(--color-accent)"
                    />
                  </svg>
                </div>
                <div className="flex flex-col">
                  <span className="text-xl font-headline font-bold text-primary leading-tight">
                    Ativanam
                  </span>
                  <span className="text-xs font-body text-textSecondary leading-tight">
                    Solutions
                  </span>
                </div>
              </Link>
            </div>

            {/* Desktop Navigation */}
            <nav className="hidden lg:flex items-center space-x-1">
              {navigationItems.map((item) => (
                <Link
                  key={item.name}
                  href={item.href}
                  className={`px-4 py-2 rounded-md text-sm font-body font-medium transition-all duration-300 ${
                    isActivePath(item.href)
                      ? 'bg-primary text-primary-foreground shadow-subtle'
                      : 'text-textPrimary hover:bg-muted hover:text-primary'
                  }`}
                >
                  {item.name}
                </Link>
              ))}
            </nav>

            {/* Emergency Contact Button - Desktop */}
            <div className="hidden lg:flex items-center space-x-4">
              <Link
                href="/contact-and-support"
                className="inline-flex items-center px-4 py-2 rounded-md bg-cta text-cta-foreground text-sm font-cta font-semibold shadow-cta transition-all duration-300 hover:-translate-y-0.5 hover:shadow-modal"
              >
                <Icon name="PhoneIcon" size={18} className="mr-2" />
                24/7 Emergency
              </Link>
            </div>

            {/* Mobile Menu Button */}
            <button
              onClick={toggleMobileMenu}
              className="lg:hidden inline-flex items-center justify-center p-2 rounded-md text-textPrimary hover:bg-muted transition-colors duration-300"
              aria-label="Toggle mobile menu"
            >
              {isMobileMenuOpen ? (
                <Icon name="XMarkIcon" size={24} />
              ) : (
                <Icon name="Bars3Icon" size={24} />
              )}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 z-40 bg-background lg:hidden"
          style={{ top: '64px' }}
        >
          <nav className="flex flex-col h-full px-4 py-6 space-y-2 overflow-y-auto">
            {navigationItems.map((item) => (
              <Link
                key={item.name}
                href={item.href}
                onClick={closeMobileMenu}
                className={`flex items-center px-4 py-3 rounded-md text-base font-body font-medium transition-all duration-300 ${
                  isActivePath(item.href)
                    ? 'bg-primary text-primary-foreground shadow-subtle'
                    : 'text-textPrimary hover:bg-muted hover:text-primary'
                }`}
              >
                {item.icon && (
                  <Icon
                    name={item.icon as any}
                    size={20}
                    className="mr-3"
                  />
                )}
                {item.name}
              </Link>
            ))}

            {/* Emergency Contact - Mobile */}
            <div className="pt-4 mt-4 border-t border-border">
              <Link
                href="/contact-and-support"
                onClick={closeMobileMenu}
                className="flex items-center justify-center px-4 py-3 rounded-md bg-cta text-cta-foreground text-base font-cta font-semibold shadow-cta transition-all duration-300"
              >
                <Icon name="PhoneIcon" size={20} className="mr-2" />
                24/7 Emergency Contact
              </Link>
            </div>

            {/* Additional Info - Mobile */}
            <div className="pt-6 mt-auto">
              <div className="flex items-center justify-center space-x-6 text-textSecondary">
                <a
                  href="tel:+1234567890"
                  className="flex items-center space-x-2 hover:text-primary transition-colors duration-300"
                >
                  <Icon name="PhoneIcon" size={18} />
                  <span className="text-sm font-body">Call Us</span>
                </a>
                <a
                  href="mailto:info@ativanam.com"
                  className="flex items-center space-x-2 hover:text-primary transition-colors duration-300"
                >
                  <Icon name="EnvelopeIcon" size={18} />
                  <span className="text-sm font-body">Email</span>
                </a>
              </div>
            </div>
          </nav>
        </div>
      )}
    </>
  );
};

export default Header;